import { calculateHours } from "./timeUtils";
import { getShiftRole, getShiftWorkerId, isShiftPayable } from "./shiftEngine";

function getShiftStart(shift) {
  return shift?.start || shift?.startTime || null;
}

function getShiftEnd(shift) {
  return shift?.end || shift?.endTime || null;
}

export function getShiftPayRate(shift) {
  const role = getShiftRole(shift);

  return (
    Number(shift?.payRate || 0) ||
    Number(shift?.rates?.payRate || 0) ||
    Number(shift?.rates?.[role]?.payRate || 0) ||
    Number(shift?.facilityRates?.[role]?.payRate || 0) ||
    Number(shift?.workerRate || 0) ||
    Number(shift?.workerPayRate || 0) ||
    0
  );
}

export function calculateShiftGrossPay(shift) {
  const hours = calculateHours(getShiftStart(shift), getShiftEnd(shift));
  const rate = getShiftPayRate(shift);

  return Math.round(hours * rate * 100) / 100;
}

export function buildPayrollLineItem(shift) {
  const start = getShiftStart(shift);
  const end = getShiftEnd(shift);
  const hours = calculateHours(start, end);
  const payRate = getShiftPayRate(shift);

  return {
    shiftId: shift?.id || shift?.shiftId || "",
    workerId: getShiftWorkerId(shift),
    facilityId: shift?.facilityId || "",
    role: getShiftRole(shift),
    start,
    end,
    hours,
    payRate,
    grossPay: Math.round(hours * payRate * 100) / 100,
  };
}

export function buildPayrollLineItems(shifts = [], facilityRatesById = {}) {
  return shifts
    .filter((shift) => isShiftPayable(shift))
    .map((shift) => {
      const facilityId = shift?.facilityId || "";
      const role = getShiftRole(shift);

      const facilityRates =
        facilityRatesById?.[facilityId] ||
        facilityRatesById?.get?.(facilityId) ||
        {};

      return buildPayrollLineItem({
        ...shift,
        facilityRates,
        payRate:
          shift?.payRate ||
          shift?.rates?.payRate ||
          facilityRates?.[role]?.payRate ||
          0,
      });
    });
}

export function calculatePayrollTotals(lineItems = []) {
  const grossPay = lineItems.reduce((total, item) => {
    return total + Number(item?.grossPay || 0);
  }, 0);

  const totalHours = lineItems.reduce((total, item) => {
    return total + Number(item?.hours || 0);
  }, 0);

  return {
    totalHours: Math.round(totalHours * 100) / 100,
    grossPay: Math.round(grossPay * 100) / 100,
  };
}

export function groupPayrollByWorker(lineItems = []) {
  return lineItems.reduce((acc, item) => {
    const workerId = item?.workerId || "unknown_worker";

    if (!acc[workerId]) {
      acc[workerId] = {
        workerId,
        totalHours: 0,
        grossPay: 0,
        lineItems: [],
      };
    }

    acc[workerId].totalHours += Number(item?.hours || 0);
    acc[workerId].grossPay += Number(item?.grossPay || 0);
    acc[workerId].lineItems.push(item);

    acc[workerId].totalHours =
      Math.round(acc[workerId].totalHours * 100) / 100;

    acc[workerId].grossPay =
      Math.round(acc[workerId].grossPay * 100) / 100;

    return acc;
  }, {});
}

export function buildPayrollPayload({
  payrollId,
  periodStart,
  periodEnd,
  shifts = [],
  facilityRatesById = {},
  createdBy,
}) {
  const lineItems = buildPayrollLineItems(shifts, facilityRatesById);
  const totals = calculatePayrollTotals(lineItems);
  const workers = groupPayrollByWorker(lineItems);

  return {
    payrollId: payrollId || "",
    periodStart: periodStart || null,
    periodEnd: periodEnd || null,
    status: "draft",
    totals,
    workers,
    lineItems,
    createdBy: createdBy || "",
    createdAt: new Date().toISOString(),
  };
}